import { useState } from "react";

import { ApiError } from "@/lib/api";
import { usePaymentMethods, useRemovePayment, useSetDefaultPayment } from "../api";

// Phase 4a customer-portal FR-9 + FR-10. Lists active payment
// sources (brand + ••••last4 + exp + default), lets the customer
// flip default + remove. Removing the only card backing an active
// rental returns 422; we surface the server's message inline.
//
// Never displays the PAN — only the gateway tokens (brand, last4,
// exp_month, exp_year, default flag).
//
// Phase 4u portal-profile-polish — saved-card rows lifted from the
// 1-line bordered list to a card with brand-consistent border, a
// flag-navy "Default" pill (matching AddressesTab), and prominent
// remove/make-default actions on the right.
export function PaymentMethodsTab(): JSX.Element {
  const q = usePaymentMethods();
  const setDefault = useSetDefaultPayment();
  const remove = useRemovePayment();
  const [removeError, setRemoveError] = useState<string | null>(null);

  if (q.isLoading) return <div className="text-sm text-zinc-600">Loading…</div>;
  if (!q.data) return <div className="text-sm text-brand-red">Couldn&rsquo;t load payment methods.</div>;

  function handleRemove(id: number): void {
    setRemoveError(null);
    remove.mutate(id, {
      onError: (e) => {
        const msg = e instanceof ApiError ? readErrorMessage(e) : e.message;
        setRemoveError(msg);
      }
    });
  }

  return (
    <div className="space-y-4">
      {q.data.rows.length === 0 ? (
        // Phase 4n empty-state polish — audit
        // `.notes/design-grades/portal/03-profile.md` liked the
        // "place a reservation to save a card" explanation but
        // asked for a Browse-equipment nudge. One card, one CTA.
        <div
          className="rounded-lg border border-dashed border-zinc-300 bg-zinc-50 p-6 text-center"
          data-testid="payment-methods-empty"
        >
          <p className="text-sm font-semibold text-brand-black">No cards on file yet</p>
          <p className="mt-1 text-sm text-zinc-600">
            We&rsquo;ll save your card at checkout so future rentals are one click.
          </p>
          <a
            href="/"
            className="mt-3 inline-flex items-center gap-1 text-sm font-semibold text-brand-red transition hover:text-brand-red-dark"
            data-testid="payment-methods-empty-cta"
          >
            Browse equipment <span aria-hidden="true">→</span>
          </a>
        </div>
      ) : (
        <ul className="space-y-2">
          {q.data.rows.map((p) => (
            <li
              key={p.id}
              className="flex items-center justify-between gap-4 rounded-lg border border-zinc-200 bg-white p-3 shadow-sm"
              data-testid={`payment-${p.id}`}
            >
              <div className="min-w-0 space-y-1">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-brand-black">
                    {p.brand} •••• {p.last4}
                  </span>
                  {p.default ? (
                    <span className="rounded-full bg-flag-navy/10 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-flag-navy">
                      Default
                    </span>
                  ) : null}
                </div>
                <p className="text-xs text-zinc-500">
                  Exp {String(p.exp_month).padStart(2, "0")}/{String(p.exp_year).slice(-2)}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-3">
                {!p.default ? (
                  <button
                    type="button"
                    onClick={() => setDefault.mutate(p.id)}
                    disabled={setDefault.isPending || remove.isPending}
                    className="text-xs font-medium text-flag-navy underline transition hover:text-brand-red disabled:opacity-40"
                    data-testid={`make-default-${p.id}`}
                  >
                    Make default
                  </button>
                ) : null}
                <button
                  type="button"
                  onClick={() => handleRemove(p.id)}
                  disabled={setDefault.isPending || remove.isPending}
                  className="text-xs font-medium text-zinc-500 underline transition hover:text-brand-red disabled:opacity-40"
                  data-testid={`remove-${p.id}`}
                >
                  Remove
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}

      {removeError ? (
        <div
          role="alert"
          className="rounded-md border border-brand-red/40 bg-brand-red/5 p-3 text-sm text-brand-red"
          data-testid="remove-error"
        >
          {removeError}
        </div>
      ) : null}

      {q.data.rows.length > 0 ? (
        <p className="text-xs text-zinc-500">
          To add another card, place a new reservation — JDR saves it on file at checkout.
        </p>
      ) : null}
    </div>
  );
}

function readErrorMessage(e: ApiError): string {
  const body = e.body as { message?: string } | null;
  return body?.message ?? "Couldn't remove this card.";
}
